import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { motion } from 'motion/react';
import {
  Typography,
  Table,
  Tag,
  Avatar,
  Spin,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';

const { Title } = Typography;

const QUERY_USERS_BY_COMPANY_MANAGER = gql`
  query QueryUsersByCompanyIdIndexManagerEmployeeId(
    $companyId: ID!
    $managerEmployeeId: String!
    $limit: Int
    $nextToken: String
  ) {
    queryUsersByCompanyIdIndexManagerEmployeeId(
      companyId: $companyId
      managerEmployeeId: $managerEmployeeId
      limit: $limit
      nextToken: $nextToken
    ) {
      nextToken
      items {
        companyId
        id
        firstName
        employeeId
        emailAddress
        accountClaimId
        active
        admin
        authMethod
        cognitoId
        careerProfile
        connectedApps
        createdById
        currency
        dateCreated
        dateFormat
        defaultDistance
        departmentId
        employeeType
        enableJobNotifications
        enableRecognition
        expirationDoneByToken
        expires
        recognitionEvents {
          additionalBudgetTotal
          amountPending
          currentBudget
          hasSeenPopup
          eventList {
            adminOverride
            budgetAfterTotal
            approvalResult
            budgetBeforeTotal
            completedById
            createdDate
            pendingApproval
            completedDate
            recognitionName
            recognitionId
            recognitionAmount
            sentById
            sentByManager
            sentReason
            snappyCampaignTierId
            snappyGrantId
          }
          lastUpdated
        }
        lastName
        language
        lastLogin
        lastMobileLogin
        languageCode
        managerEmployeeId
        title
      }
    }
  }
`;


const MyTeamComponent = (props) => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const employeeId = currentUser?.employeeId;
  const companyId = currentUser?.company?.id;
  const baseSnappyBudget = currentUser?.company?.recognitionSettings?.baseSnappyBudget || 0;
  
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    if (employeeId && companyId) {
      fetchTeamData();
    }
  }, [employeeId, companyId]);

  const sort = (a = '', b = '') => {
			if (!a) return -1;
			if (!b) return 1;
			if (typeof a === 'string') {
				a = a.toLowerCase();
			}

			if (typeof b === 'string') {
				b = b.toLowerCase();
			}

			if (a > b) return 1;
			if (a < b) return -1;
			return 0;
		};

  const fetchTeamData = async () => {
    if (employeeId && companyId) {
      setLoading(true);
      try {
        const { data: teamData } = await props.client.query({
          query: QUERY_USERS_BY_COMPANY_MANAGER,
          variables: {
            companyId: companyId,
            managerEmployeeId: employeeId,
            limit: 1000,
          },
          fetchPolicy: 'network-only'
        });

        if (teamData?.queryUsersByCompanyIdIndexManagerEmployeeId?.items) {
          setTeamMembers(teamData.queryUsersByCompanyIdIndexManagerEmployeeId.items);
        }
      } catch (error) {
        console.error('Error fetching team data:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const teamMembersColumns = [
    {
      title: 'Employee',
      dataIndex: 'name',
      key: 'name',
      defaultSortOrder: 'ascend',
      sorter: (a,b) => sort(a.firstName,b.firstName),
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            icon={<UserOutlined />}
            src={record.additionalInfo?.avatar?.key ? `path-to-avatar/${record.additionalInfo.avatar.key}` : null}
            style={{ marginRight: 8 }}
          />
          <span>{`${record.firstName} ${record.lastName}`}</span>
        </div>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Email',
      dataIndex: 'emailAddress',
      key: 'email',
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'status',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Budget',
      dataIndex: 'additionalInfo',
      key: 'budget',
      render: (additionalInfo) => {
        const currentBudget = additionalInfo?.recognitionEvents?.currentBudget || 0;
        const totalBudget = currentBudget + baseSnappyBudget;
        return (
          <span style={{ color: totalBudget > 0 ? 'green' : 'red' }}>
            ${totalBudget}
          </span>
        );
      },
    }
  ];

  return (
    <div className="my-team-container">
      <Title level={2}>My Team Dashboard</Title>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <Table
              dataSource={teamMembers}
              columns={teamMembersColumns}
              rowKey="id"
              showSorterTooltip={false}
              pagination={{ pageSize: 10 }}
            />
          </motion.div>
        </>
      )}
    </div>
  );
};

export default withApollo(MyTeamComponent);